/**
 * URL Filter Utilities
 * Handles synchronization between URL parameters and filter choices
 *
 * URL Format (simplified - one key=value per filter):
 * - City: ?city=cityID
 * - Area: ?area=areaID
 * - Activity: ?activity=activityName
 * - Population: ?population=populationName
 */

/**
 * Parse URL search params and convert to filterChoices format
 * Note: Only IDs/names are stored in URL. Full details are looked up from filter data.
 * @returns {Object|null} filterChoices object or null if no filters in URL
 */
export const getFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);

    const filterChoices = {};
    let hasFilters = false;

    // Check for city filter (filterTypeID: 1)
    if (params.has('city')) {
        hasFilters = true;
        filterChoices[1] = {
            filterTypeID: 1,
            title: 'איפה תרצו להתנדב?',
            choice: {
                cityID: params.get('city'),
                _fromUrl: true // Flag to indicate this needs lookup
            }
        };
    }
    // Check for area filter (also filterTypeID: 1, but different structure)
    else if (params.has('area')) {
        hasFilters = true;
        filterChoices[1] = {
            filterTypeID: 1,
            title: 'איפה תרצו להתנדב?',
            choice: {
                areaID: params.get('area'),
                _fromUrl: true
            }
        };
    }

    // Check for activity field filter (filterTypeID: 6)
    if (params.has('activity')) {
        hasFilters = true;
        filterChoices[6] = {
            filterTypeID: 6,
            title: 'איך תרצו להתנדב?',
            choice: {
                name: params.get('activity'),
                id: params.get('activity'),
                _fromUrl: true
            }
        };
    }

    // Check for population filter (filterTypeID: 7)
    if (params.has('population')) {
        hasFilters = true;
        filterChoices[7] = {
            filterTypeID: 7,
            title: 'למי תרצו לעזור?',
            choice: {
                name: params.get('population'),
                id: params.get('population'),
                _fromUrl: true
            }
        };
    }

    return hasFilters ? filterChoices : null;
};

/**
 * Update URL with current filter choices (simplified - one param per filter)
 * @param {Object} filterChoices - The current filter choices from Redux store
 */
export const updateUrlWithFilters = (filterChoices) => {
    const params = new URLSearchParams();

    if (!filterChoices) {
        if (window.location.search) {
            window.history.replaceState({}, '', window.location.pathname);
        }
        return;
    }

    // Location filter (filterTypeID: 1)
    const locationFilter = filterChoices[1];
    if (locationFilter?.choice) {
        const choice = locationFilter.choice;
        if (choice.cityID) {
            params.set('city', choice.cityID);
        } else if (choice.areaID) {
            params.set('area', choice.areaID);
        }
    }

    // Activity field filter (filterTypeID: 6)
    const activityFilter = filterChoices[6];
    if (activityFilter?.choice) {
        params.set('activity', activityFilter.choice.name || activityFilter.choice.id);
    }

    // Population filter (filterTypeID: 7)
    const populationFilter = filterChoices[7];
    if (populationFilter?.choice) {
        params.set('population', populationFilter.choice.name || populationFilter.choice.id);
    }

    // Update URL
    const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

    if (window.location.search !== `?${params.toString()}` &&
        !(window.location.search === '' && params.toString() === '')) {
        window.history.replaceState({}, '', newUrl);
    }
};

/**
 * Merge URL filters with existing filterChoices
 * URL filters take priority
 * @param {Object} existingChoices - Current filter choices from store/localStorage
 * @param {Object} urlFilters - Filters parsed from URL
 * @returns {Object} Merged filter choices
 */
export const mergeFiltersWithUrl = (existingChoices, urlFilters) => {
    if (!urlFilters) return existingChoices;
    if (!existingChoices) return urlFilters;

    // URL filters override existing ones
    return {
        ...existingChoices,
        ...urlFilters
    };
};

/**
 * Check if URL has any filter parameters
 * @returns {boolean}
 */
export const hasUrlFilters = () => {
    const params = new URLSearchParams(window.location.search);
    return params.has('city') || params.has('area') || params.has('activity') || params.has('population');
};
